import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Plus, CheckCircle2, Circle, Trash2, CalendarClock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDue = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const isOverdue = (t) => t.status === 'open' && t.due_at && new Date(t.due_at) < new Date();

const RelatedTasks = ({ relatedType, relatedId, relatedName }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { params: { related_type: relatedType, related_id: relatedId, status: 'all' } });
      setTasks(res.data.tasks || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [relatedType, relatedId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/tasks`, {
        title: title.trim(),
        due_at: due ? new Date(due).toISOString() : null,
        related_type: relatedType, related_id: relatedId,
      });
      setTitle(''); setDue('');
      fetchTasks();
      toast.success('Task added');
    } catch { toast.error('Failed to add task'); }
    finally { setAdding(false); }
  };

  const toggle = async (t) => {
    try {
      await axios.post(`${API}/tasks/${t.id}/${t.status === 'open' ? 'complete' : 'reopen'}`);
      fetchTasks();
    } catch { toast.error('Failed'); }
  };
  const remove = async (t) => {
    try { await axios.delete(`${API}/tasks/${t.id}`); fetchTasks(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="border-t border-slate-800 pt-4" data-testid="related-tasks">
      <Label className="text-slate-300 mb-2 block">Tasks</Label>
      <form onSubmit={addTask} className="flex flex-wrap gap-2 mb-3">
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a task…" className="bg-slate-950 border-slate-800 h-9 flex-1 min-w-[160px]" data-testid="related-task-title" />
        <Input type="datetime-local" value={due} onChange={e => setDue(e.target.value)} className="bg-slate-950 border-slate-800 h-9 w-[200px]" data-testid="related-task-due" />
        <Button type="submit" size="sm" disabled={adding || !title.trim()} className="bg-violet-600 hover:bg-violet-500 h-9" data-testid="related-task-add">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </form>
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-600" /></div>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-slate-600 italic text-center py-3">No tasks yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
          {tasks.map(t => (
            <div key={t.id} className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-md px-2.5 py-2" data-testid={`related-task-${t.id}`}>
              <button onClick={() => toggle(t)} className="shrink-0" data-testid={`related-task-toggle-${t.id}`}>
                {t.status === 'completed'
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  : <Circle className="w-4 h-4 text-slate-400 hover:text-violet-600" />}
              </button>
              <div className="min-w-0 flex-1">
                <p className={`text-sm truncate ${t.status === 'completed' ? 'line-through text-slate-500' : 'text-white'}`}>{t.title}</p>
                {t.due_at && (
                  <span className={`text-[11px] flex items-center gap-1 ${isOverdue(t) ? 'text-rose-500' : 'text-slate-500'}`}>
                    <CalendarClock className="w-3 h-3" /> {fmtDue(t.due_at)}
                  </span>
                )}
              </div>
              <button onClick={() => remove(t)} className="text-slate-400 hover:text-rose-500 shrink-0" data-testid={`related-task-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelatedTasks;
